import type { Plan } from '@/services/api/api.types'

import { FREE_PLAN_PRICE } from './onboardingConstants'

/** A plan sold per user, as opposed to a credit pack. */
export type SeatPlan = Plan & { maxUsers: number }

/**
 * Keeps only the per-user seat plans. Credit packs share the bundles table but carry no
 * maxUsers, so anything that compares a user count against a tier must never see them.
 * Mirrors BillingPlan.seat_plans() in backend_common_functions.
 */
export function getSeatPlans(plans: Plan[]): SeatPlan[] {
  return plans.filter((plan): plan is SeatPlan => plan.planType !== 'credit_pack' && plan.maxUsers != null)
}

/**
 * The active seat tiers, cheapest first. The unlimited tier carries maxUsers 0 rather than a
 * large number, so it sorts last by hand. Callers rely on the order being contiguous:
 * calculateUserRanges reads each tier's neighbour to build its band.
 */
export function getActiveSeatTiersSorted(plans: Plan[]): SeatPlan[] {
  return getSeatPlans(plans)
    .filter(plan => plan.active)
    .sort((a, b) => {
      const aMax = a.maxUsers === 0 ? Infinity : a.maxUsers
      const bMax = b.maxUsers === 0 ? Infinity : b.maxUsers
      return aMax - bMax
    })
}

/**
 * Gets the free plan from a list of plans
 */
export function getFreePlan(plans: Plan[]): Plan | undefined {
  return plans.find(plan => plan.priceDollars === FREE_PLAN_PRICE)
}

/**
 * Formats price for display
 */
export function formatPlanPrice(dollars: number): string {
  return dollars === FREE_PLAN_PRICE ? 'Free' : `$${Math.round(dollars)}`
}

/**
 * Formats max users for display
 */
export function formatMaxUsers(maxUsers: number): string {
  if (maxUsers === 0) {
    return 'Unlimited users'
  }
  return `Up to ${maxUsers.toLocaleString()} users`
}

/**
 * Formats a user range for display (e.g., "0-10 users", "11-50 users", "500+ users")
 */
export function formatUserRange(minUsers: number, maxUsers: number): string {
  if (maxUsers === 0) {
    return `${minUsers.toLocaleString()}+ users`
  }
  return `${minUsers.toLocaleString()}-${maxUsers.toLocaleString()} users`
}

/**
 * Calculates user ranges for a sorted list of plans.
 * Returns an array of { min, max } objects corresponding to each plan.
 */
export function calculateUserRanges(sortedPlans: SeatPlan[]): Array<{ min: number; max: number }> {
  return sortedPlans.map((plan, index) => {
    const previousMax = index === 0 ? 0 : sortedPlans[index - 1].maxUsers
    const min = previousMax === 0 ? previousMax : previousMax + 1
    return { min, max: plan.maxUsers }
  })
}

/**
 * Calculates the savings percentage compared to a baseline price
 */
export function calculateSavingsPercent(price: number, baselinePrice: number): number {
  if (baselinePrice <= 0 || price <= 0 || price >= baselinePrice) {
    return 0
  }
  return Math.round(((baselinePrice - price) / baselinePrice) * 100)
}

/**
 * Calculates how many seats until the next tier
 */
export function calculateSeatsUntilNextTier(currentUsers: number, currentPlanMaxUsers: number): number | null {
  if (currentPlanMaxUsers === 0) {
    return null
  }
  return Math.max(0, currentPlanMaxUsers - currentUsers + 1)
}
